import { deferShellRender } from '../ui/shell_input_guard.mjs';
// Renders the global toolbar (the "Projekt" / "Rasterdarstellung" /
// "Grundriss/Layout" / … command-card row above the plan canvas) from a
// DB-driven config instead of the old hard-coded index.html markup — see
// #globalMenuToolbar in index.html and GLOBAL_MENU_DEFAULT_CONFIG below for
// the fallback used when /api/toolbar-config can't be reached.
//
// A seeded button's `code` is rendered as the control's DOM `id`, matching
// the exact ids the old hard-coded HTML used (btnUndo, chkSnap, selGridSize,
// …) — a wide range of unrelated, pre-existing modules
// (project_controls.js, obstacles.js, dimension_service.js, global_levels.js,
// text_annotation_service.js, sketch_wall_interaction.js, object_library.js,
// floorplan_controller.js) look these ids up directly via getElementById to
// sync active/disabled state or attach their own listeners; renaming a
// seeded button's `code` breaks that specific integration (see the schema
// comment in backend/schema.sql).
//
// Datei-Uploads werden als label+input gerendert und ebenso wie Buttons über
// GLOBAL_MENU_ACTIONS delegiert. Dadurch funktionieren sie auch innerhalb
// eines erst beim Klick erzeugten Auswahlmodals.
import { GLOBAL_MENU_ACTIONS } from './global_menu_actions.js?v=20260904f';
import { globalMenuIconContentHtml } from './global_menu_icon_registry.js?v=20260820d';
import { GLOBAL_MENU_DEFAULT_CONFIG } from './global_menu_default_config.js?v=20260820d';
import { getActiveLanguage } from '../i18n/i18n_service.js?v=20260722a';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';

const GLOBAL_MENU_SELECTION_GROUPS = Object.freeze({
  floorplan_actions: Object.freeze({
    triggerCode: 'btnFloorplanSelection',
    triggerLabel: 'Grundrissplan',
    triggerIconKey: 'floorplan',
    actionCodes: Object.freeze([
      'load-floorplan-file',
      'satellite-floorplan',
      'load-cad-floorplan-file'
    ])
  }),
  export_actions: Object.freeze({
    triggerCode: 'btnExportSelection',
    triggerLabel: 'Auswahl',
    useLocalizedSelectionLabel: true,
    actionCodes: Object.freeze([
      'dxf-export',
      'glb-export',
      '3dpdf-export',
      'wireframe-export',
      'step-export'
    ])
  })
});
const GLOBAL_MENU_SELECTION_TEXT = Object.freeze({
  DE: Object.freeze({ selection: 'Auswahl', close: 'Schliessen', unavailable: 'Keine Aktionen verfügbar.' }),
  FR: Object.freeze({ selection: 'Sélection', close: 'Fermer', unavailable: 'Aucune action disponible.' }),
  EN: Object.freeze({ selection: 'Selection', close: 'Close', unavailable: 'No actions available.' }),
  IT: Object.freeze({ selection: 'Selezione', close: 'Chiudi', unavailable: 'Nessuna azione disponibile.' })
});

function globalMenuSelectionText() {
  return GLOBAL_MENU_SELECTION_TEXT[String(getActiveLanguage() || 'DE').toUpperCase()]
    || GLOBAL_MENU_SELECTION_TEXT.DE;
}

function parseOptions(button) {
  if (Array.isArray(button.options_json)) return button.options_json;
  if (typeof button.options_json === 'string' && button.options_json) {
    try {
      const parsed = JSON.parse(button.options_json);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

// A checkbox's live value is a real boolean from the action registry, or
// the DB's string 'true'/'false' fallback when no project value exists yet
// (see the module doc comment for why the DB value is only ever a fallback).
function currentControlValue(button) {
  const entry = GLOBAL_MENU_ACTIONS[button.action_code];
  if (entry && typeof entry.getValue === 'function') return entry.getValue();
  return button.default_value;
}

function iconHtml(button) {
  if (button.icon_path) return `<img src="${escapeHtml(button.icon_path)}" alt="" loading="lazy" decoding="async">`;
  if (button.icon_key) return globalMenuIconContentHtml(button.icon_key);
  return '';
}

// The per-icon color (grey undo/redo, orange new-order, green save, red
// delete, …) comes entirely from a `.icon-btn.<key>-icon` CSS rule in
// styles.css keyed on the SAME icon_key used to look up the SVG — dropping
// this class was the original migration bug (icons rendered, but all in
// one default color). Only applies to a real registry icon_key; an
// uploaded icon_path image carries its own color already.
function iconClassSuffix(button) {
  if (button.icon_path || !button.icon_key) return '';
  return ` ${escapeHtml(button.icon_key)}-icon`;
}

function globalMenuControlHtml(button, hiddenAttr = '', showIconLabel = false) {
  const code = escapeHtml(button.code);
  const label = escapeHtml(button.label);
  const actionCode = escapeHtml(button.action_code);

  if (button.action_code === 'load-floorplan-file' || button.action_code === 'load-cad-floorplan-file') {
    const accept = button.action_code === 'load-floorplan-file' ? 'image/*,.pdf' : '.dxf,.dwg';
    const icon = iconHtml(button);
    const visibleContent = icon
      ? `${icon}${showIconLabel ? `<span class="global-menu-action-label">${label}</span>` : ''}`
      : label;
    return `<label class="btn file-btn${icon ? ` icon-btn${iconClassSuffix(button)}` : ''}" aria-label="${label}" title="${label}">${visibleContent}<input id="${code}" type="file" accept="${accept}" data-action-code="${actionCode}" hidden></label>`;
  }

  if (button.control_type === 'checkbox') {
    const raw = currentControlValue(button);
    const checked = raw === true || raw === 'true';
    // chkHelperGrid's "#" is coloured to match the helper grid line stroke
    // (#fb7185, see drawGrid() in plan_renderer.js) so the checkbox reads as
    // "this controls the red grid", not a generic option.
    const labelHtml = button.code === 'chkHelperGrid'
      ? `<span class="grid-helper-hash-label">${label}</span>`
      : label;
    return `<label${hiddenAttr}><input type="checkbox" id="${code}" data-action-code="${actionCode}" ${checked ? 'checked' : ''}> ${labelHtml}</label>`;
  }

  if (button.control_type === 'dropdown') {
    const options = parseOptions(button);
    const currentValue = String(currentControlValue(button) ?? '');
    const optionsHtml = options.map(option => {
      const value = escapeHtml(option.value);
      const selected = String(option.value) === currentValue ? ' selected' : '';
      return `<option value="${value}"${selected}>${escapeHtml(option.label ?? option.value)}</option>`;
    }).join('');
    return `<select id="${code}" data-action-code="${actionCode}" aria-label="${label}"${hiddenAttr}>${optionsHtml}</select>`;
  }

  if (button.control_type === 'input') {
    const inputType = button.input_kind === 'number' ? 'number' : 'text';
    const currentValue = escapeHtml(currentControlValue(button) ?? '');
    const numberAttrs = inputType === 'number' ? ' min="0" step="10"' : '';
    return `<label class="global-menu-input-field">${label}<input id="${code}" data-action-code="${actionCode}" type="${inputType}"${numberAttrs} value="${currentValue}" aria-label="${label}"></label>`;
  }

  // Plain button: icon-only (matching the old icon buttons, label becomes
  // the accessible name) when an icon is set, otherwise visible text.
  const icon = iconHtml(button);
  const visibleLabel = icon
    ? (showIconLabel ? `<span class="global-menu-action-label">${label}</span>` : '')
    : label;
  const iconClass = icon ? ` icon-btn${iconClassSuffix(button)}` : '';
  return `<button type="button" id="${code}" class="btn${iconClass}" data-action-code="${actionCode}" aria-label="${label}" title="${label}"${hiddenAttr}>${icon}${visibleLabel}</button>`;
}

// project_controls.js#renderFloorplanControls() writes contextual floorplan
// status (e.g. "Bild geladen, 4.2 MB") into this slot on every refresh — it
// isn't a configurable button, so it isn't part of the DB structure at all,
// but it needs to keep rendering inside the "Grundriss/Layout" card, so it's
// appended here unconditionally whenever that specific card renders.
function trailingSlotHtml(card) {
  if (card.code !== 'floorplan_actions') return '';
  return '<div id="floorplanStatusControls" class="floorplan-status-controls"></div>';
}

function globalMenuSelectionButtons(card, buttons) {
  const group = GLOBAL_MENU_SELECTION_GROUPS[card?.code];
  if (!group) return [];
  const actionCodes = new Set(group.actionCodes);
  return buttons.filter(button => actionCodes.has(button.action_code));
}

function globalMenuSelectionTriggerHtml(card, buttons) {
  const group = GLOBAL_MENU_SELECTION_GROUPS[card?.code];
  if (!group || !buttons.length) return '';
  const rawLabel = group.useLocalizedSelectionLabel
    ? (globalMenuSelectionText().selection || group.triggerLabel)
    : group.triggerLabel;
  const label = escapeHtml(rawLabel);
  const icon = group.triggerIconKey ? globalMenuIconContentHtml(group.triggerIconKey) : '';
  const iconClass = icon ? ` icon-btn ${escapeHtml(group.triggerIconKey)}-icon` : '';
  return `<button type="button" id="${escapeHtml(group.triggerCode)}" class="btn${iconClass}" data-global-menu-selection="${escapeHtml(card.code)}" aria-haspopup="dialog" aria-controls="globalMenuSelectionModal" aria-expanded="false" aria-label="${label}" title="${label}">${icon || label}</button>`;
}

// grid_actions ("Rasterdarstellung"): selGridSize/chkSnap only make sense
// once chkGrid is on, helperGridSize only once chkHelperGrid is on — saves
// toolbar space by hiding controls that would otherwise have no effect.
// Kept live in sync by direct hidden-attribute toggling in the
// grid-toggle/helper-grid-toggle handlers (global_menu_actions.js), since
// this card is only rendered once at startup, not on every refresh.
const GRID_ACTIONS_DEPENDENT_CODES = Object.freeze({
  selGridSize: 'chkGrid',
  chkSnap: 'chkGrid',
  helperGridSize: 'chkHelperGrid'
});

function gridActionsHiddenAttr(card, button, buttons) {
  if (card.code !== 'grid_actions') return '';
  const controllerCode = GRID_ACTIONS_DEPENDENT_CODES[button.code];
  if (!controllerCode) return '';
  const controller = buttons.find(candidate => candidate.code === controllerCode);
  if (!controller) return '';
  const raw = currentControlValue(controller);
  const controllerChecked = raw === true || raw === 'true';
  return controllerChecked ? '' : ' hidden';
}

function globalMenuControlHiddenAttr(card, button, buttons) {
  const gridHidden = gridActionsHiddenAttr(card, button, buttons);
  if (gridHidden) return gridHidden;
  const action = GLOBAL_MENU_ACTIONS[button.action_code];
  if (typeof action?.isVisible === 'function' && !action.isVisible()) return ' hidden';
  return '';
}

function globalMenuCardHtml(card, buttons) {
  const selectionButtons = globalMenuSelectionButtons(card, buttons);
  const selectionButtonCodes = new Set(selectionButtons.map(button => button.code));
  const selectionTrigger = globalMenuSelectionTriggerHtml(card, selectionButtons);
  let selectionTriggerRendered = false;
  const controlsHtml = buttons.map(button => {
    if (!selectionButtonCodes.has(button.code)) return globalMenuControlHtml(button, globalMenuControlHiddenAttr(card, button, buttons));
    if (selectionTriggerRendered) return '';
    selectionTriggerRendered = true;
    return selectionTrigger;
  }).join('');
  return `<div class="command-card" data-toolbar-card="${escapeHtml(card.code)}" aria-label="${escapeHtml(card.label)}">` +
    `<span class="command-card-title">${escapeHtml(card.label)}</span>` +
    `<div class="command-card-body">${controlsHtml}${trailingSlotHtml(card)}</div>` +
    `</div>`;
}

function resolvedGlobalMenuCards(config) {
  const cards = Array.isArray(config?.cards) ? config.cards : [];
  const buttons = Array.isArray(config?.buttons) ? config.buttons : [];
  const cardButtons = Array.isArray(config?.cardButtons) ? config.cardButtons : [];
  const buttonsByCode = new Map(buttons.map(button => [button.code, button]));

  const buttonCodesByCard = new Map();
  for (const link of cardButtons) {
    if (!buttonCodesByCard.has(link.cardCode)) buttonCodesByCard.set(link.cardCode, []);
    buttonCodesByCard.get(link.cardCode).push(link.buttonCode);
  }

  return cards.map(card => ({
    card,
    buttons: (buttonCodesByCard.get(card.code) || [])
      .map(code => buttonsByCode.get(code))
      .filter(Boolean)
  }));
}

// Pure function: config in, HTML string out — kept side-effect-free so it's
// directly unit-testable without a DOM (see test/logic/global_menu_renderer.test.mjs).
function globalMenuToolbarHtml(config) {
  return resolvedGlobalMenuCards(config)
    .map(({ card, buttons }) => globalMenuCardHtml(card, buttons))
    .join('');
}

let toolbarEventsBound = false;
let currentToolbarConfig = null;
let activeGlobalMenuSelectionModal = null;

function globalMenuSelectionModalHtml(card, buttons) {
  const selectionButtons = globalMenuSelectionButtons(card, buttons);
  const text = globalMenuSelectionText();
  const group = GLOBAL_MENU_SELECTION_GROUPS[card?.code];
  const selectionLabel = group?.useLocalizedSelectionLabel ? text.selection : group?.triggerLabel;
  const title = `${card.label || 'Auswahl'} – ${selectionLabel || text.selection}`;
  const showIconLabels = card?.code === 'floorplan_actions';
  const actionsHtml = selectionButtons.length
    ? selectionButtons.map(button => globalMenuControlHtml(button, '', showIconLabels)).join('')
    : `<p class="global-menu-selection-unavailable">${escapeHtml(text.unavailable)}</p>`;
  return `
    <div id="globalMenuSelectionModal" class="modal global-menu-selection-modal" role="dialog" aria-modal="true" aria-labelledby="globalMenuSelectionModalTitle">
      <div class="global-menu-selection-header">
        <div id="globalMenuSelectionModalTitle" class="modal-title">${escapeHtml(title)}</div>
        <button type="button" class="btn" data-global-menu-selection-close>${escapeHtml(text.close)}</button>
      </div>
      <div class="global-menu-selection-actions">${actionsHtml}</div>
    </div>
  `;
}

function dispatchGlobalMenuButtonClick(event, actions = GLOBAL_MENU_ACTIONS) {
  const target = event.target.closest('[data-action-code]');
  if (!target || target.tagName === 'INPUT' || target.tagName === 'SELECT') return null;
  const entry = actions[target.dataset.actionCode];
  if (!entry || entry.kind !== 'button') return null;
  event.preventDefault();
  return entry.onClick(target);
}

function dispatchGlobalMenuControlChange(event, actions = GLOBAL_MENU_ACTIONS) {
  const target = event.target.closest('[data-action-code]');
  if (!target) return null;
  const entry = actions[target.dataset.actionCode];
  if (!entry) return null;
  if (entry.kind === 'checkbox') return entry.onChange(target.checked);
  if (entry.kind === 'dropdown' || entry.kind === 'input') return entry.onChange(target.value);
  if (entry.kind === 'file') {
    const file = target.files?.[0];
    if (!file) return null;
    const result = entry.onChange(file, target);
    target.value = '';
    return result;
  }
  return null;
}

function closeGlobalMenuSelectionModal() {
  const active = activeGlobalMenuSelectionModal;
  if (!active) return;
  activeGlobalMenuSelectionModal = null;
  active.returnFocus?.setAttribute?.('aria-expanded', 'false');
  active.root.remove();
  if (active.returnFocus?.isConnected) active.returnFocus.focus();
}

function focusGlobalMenuSelectionModal(root) {
  root?.querySelector('.global-menu-selection-actions .btn')?.focus();
}

function openGlobalMenuSelectionModal(cardCode, returnFocus) {
  if (activeGlobalMenuSelectionModal) {
    focusGlobalMenuSelectionModal(activeGlobalMenuSelectionModal.root);
    return;
  }
  const resolved = resolvedGlobalMenuCards(currentToolbarConfig)
    .find(entry => entry.card.code === cardCode);
  if (!resolved) return;
  if (!globalMenuSelectionButtons(resolved.card, resolved.buttons).length) return;
  const modalHtml = globalMenuSelectionModalHtml(resolved.card, resolved.buttons);

  const root = document.createElement('div');
  root.className = 'modal-backdrop global-menu-selection-modal-root';
  root.innerHTML = modalHtml;
  root.addEventListener('click', event => {
    if (event.target.closest('[data-global-menu-selection-close]')) {
      closeGlobalMenuSelectionModal();
      return;
    }
    // Jede Exportaktion laeuft im Hintergrund weiter (eigene Statusmeldung,
    // siehe view3d_controller.js) -- das kleine Auswahlmodal muss dafuer
    // nicht offen bleiben. Nutzerwunsch 2026-08-18: sofortiger Klick-dann-
    // schliessen-Ablauf statt der bisherigen Busy-Sperre, die den Nutzer bis
    // zum Abschluss im Modal festhielt.
    const target = event.target.closest('[data-action-code]');
    if (!target || target.tagName === 'INPUT' || target.tagName === 'SELECT') return;
    dispatchGlobalMenuButtonClick(event);
    closeGlobalMenuSelectionModal();
  });
  root.addEventListener('change', event => {
    const target = event.target.closest('[data-action-code]');
    const isFile = target?.type === 'file' && Boolean(target.files?.[0]);
    dispatchGlobalMenuControlChange(event);
    if (isFile) closeGlobalMenuSelectionModal();
  });
  document.body.appendChild(root);
  activeGlobalMenuSelectionModal = { root, returnFocus, cardCode };
  returnFocus?.setAttribute?.('aria-expanded', 'true');
  focusGlobalMenuSelectionModal(root);
}

function refreshActiveGlobalMenuSelectionModal(host) {
  const active = activeGlobalMenuSelectionModal;
  if (!active) return;
  const resolved = resolvedGlobalMenuCards(currentToolbarConfig)
    .find(entry => entry.card.code === active.cardCode);
  const fallbackCard = { code: active.cardCode, label: 'Export' };
  active.root.innerHTML = globalMenuSelectionModalHtml(
    resolved?.card || fallbackCard,
    resolved?.buttons || []
  );
  active.returnFocus = host.querySelector(`[data-global-menu-selection="${active.cardCode}"]`);
  active.returnFocus?.setAttribute?.('aria-expanded', 'true');
  focusGlobalMenuSelectionModal(active.root);
}

function bindGlobalMenuToolbarEvents(container) {
  if (toolbarEventsBound) return;
  toolbarEventsBound = true;
  container.addEventListener('click', event => {
    const selectionTrigger = event.target.closest('[data-global-menu-selection]');
    if (selectionTrigger) {
      event.preventDefault();
      openGlobalMenuSelectionModal(selectionTrigger.dataset.globalMenuSelection, selectionTrigger);
      return;
    }
    dispatchGlobalMenuButtonClick(event);
  });
  container.addEventListener('change', dispatchGlobalMenuControlChange);
}

function renderGlobalMenuToolbar(config) {
  const host = document.getElementById('globalMenuToolbar');
  if (!host) return;
  currentToolbarConfig = config;
  if (deferShellRender('global-toolbar', () => renderGlobalMenuToolbar(currentToolbarConfig))) return;
  host.innerHTML = globalMenuToolbarHtml(config);
  bindGlobalMenuToolbarEvents(host);
  refreshActiveGlobalMenuSelectionModal(host);
}

// Blocking (awaited) by design, unlike the header config's fire-and-forget
// overlay: the toolbar's controls must exist with their final ids before
// initApp() synchronisiert und Event-Delegation aktiviert. On any failure —
// network error, unmigrated DB, fresh install —
// falls back to GLOBAL_MENU_DEFAULT_CONFIG so the toolbar is never left
// blank.
async function loadAndApplyGlobalMenuConfig() {
  try {
    const response = await fetch(`/api/toolbar-config?lang=${encodeURIComponent(getActiveLanguage())}`);
    const payload = response.ok ? await response.json() : null;
    if (payload?.ok) {
      renderGlobalMenuToolbar(payload);
      return;
    }
    throw new Error('invalid toolbar-config payload');
  } catch (error) {
    console.warn('KS Studio: Werkzeugleisten-Konfiguration konnte nicht geladen werden, verwende Standard-Layout.', error);
    renderGlobalMenuToolbar(GLOBAL_MENU_DEFAULT_CONFIG);
  }
}

export {
  dispatchGlobalMenuButtonClick,
  dispatchGlobalMenuControlChange,
  globalMenuSelectionButtons,
  globalMenuSelectionModalHtml,
  globalMenuToolbarHtml,
  renderGlobalMenuToolbar,
  loadAndApplyGlobalMenuConfig
};
